import { useState, useEffect } from "react";
import { Menu, X, Phone, Mail } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LangContext";

const LOGO_URL = "/logo-blanc.png";

export default function Header() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: t.nav.accueil, href: "#accueil" },
    { label: t.nav.experiences, href: "#experiences" },
    { label: t.nav.forfaits, href: "#forfaits" },
    { label: t.nav.location, href: "#location" },
    { label: t.nav.voiceover, href: "#voiceover" },
    { label: t.nav.soumission, href: "#soumission" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/80 border-b border-red-900/30 shadow-lg shadow-red-900/10"
          : "bg-gradient-to-b from-black/70 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
        <a href="#accueil" onClick={(e) => { e.preventDefault(); scrollTo("#accueil"); }} className="flex items-center group" data-testid="logo-link">
          <motion.img
            src={LOGO_URL}
            alt="Les Productions DJMIX.CA"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="h-8 sm:h-9 md:h-11 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
            style={{ filter: "drop-shadow(0 2px 12px rgba(230,0,0,0.25))" }}
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <div className="hidden items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[#E60000] flex items-center justify-center font-black text-white text-sm">DJ</div>
            <span className="font-heading text-white text-lg font-bold uppercase">DJMIX<span className="text-[#E60000]">.CA</span></span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="text-xs text-[#A3A3A3] hover:text-white uppercase tracking-[0.2em] font-medium transition-colors duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#E60000] hover:after:w-full after:transition-all after:duration-300"
              data-testid={`nav-${link.href.replace("#", "")}`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5 ml-8">
          {/* Language toggle */}
          <button
            onClick={toggle}
            className="text-xs font-bold uppercase tracking-wider border border-white/20 hover:border-[#E60000] px-3 py-1.5 text-[#A3A3A3] hover:text-white transition-all ml-4"
            data-testid="lang-toggle"
          >
            {lang === "fr" ? "EN" : "FR"}
          </button>

          <a href="tel:+15144330846" className="text-[#A3A3A3] hover:text-[#E60000] transition-colors" data-testid="header-phone">
            <Phone size={18} />
          </a>
          <a href="mailto:studio@djmix.ca" className="text-[#A3A3A3] hover:text-[#E60000] transition-colors" data-testid="header-email">
            <Mail size={18} />
          </a>
          <button
            onClick={() => scrollTo("#soumission")}
            className="bg-[#E60000] hover:bg-[#FF3333] text-white text-xs font-bold uppercase tracking-[0.2em] px-6 py-3 transition-all duration-300 hover:shadow-lg hover:shadow-red-900/40"
            data-testid="header-cta"
          >
            {t.nav.soumission}
          </button>
        </div>

        <div className="flex lg:hidden items-center gap-3">
          <button onClick={toggle} className="text-xs font-bold uppercase border border-white/20 px-2 py-1 text-[#A3A3A3]" data-testid="lang-toggle-mobile">
            {lang === "fr" ? "EN" : "FR"}
          </button>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="text-white p-2" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-red-900/30 overflow-hidden" data-testid="mobile-menu">
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <button key={link.href} onClick={() => scrollTo(link.href)} className="text-left text-white uppercase tracking-widest text-sm font-medium py-2 border-b border-white/5 hover:text-[#E60000] transition-colors">
                  {link.label}
                </button>
              ))}
              <a href="tel:+15144330846" className="text-[#E60000] flex items-center gap-2 text-sm pt-2">
                <Phone size={16} /> 514 433-0846
              </a>
              <button onClick={() => scrollTo("#soumission")} className="bg-[#E60000] text-white text-sm font-bold uppercase tracking-wider px-5 py-3 mt-2" data-testid="mobile-cta">
                {t.nav.soumission}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
